// Seed data — literals matching the prototype exactly (Phase 0.4).
//
// These are the cold-start / calibration values. Every field here corresponds
// to a `source` of ESTIMATE or GUIDANCE until a live ingest upgrades it. The
// base case built from this data MUST reconcile to 0.00% (INV-1).
//
// DO NOT "correct" these into calendar years or tidy them up. Oracle (FY ends
// May), NVIDIA (late Jan) and Microsoft (June) figures are already fiscal-year
// numbers; changing them silently re-pins the base case. See CLAUDE.md §11.

import type {
  BaseYields,
  Coefficients,
  Drivers,
  Hyperscaler,
  Lab,
  Supplier,
  Tier,
  World,
} from "./entities.ts";

export const LABS: Lab[] = [
  { id: "openai", name: "OpenAI", compute: 50 },
  { id: "anthropic", name: "Anthropic", compute: 32 },
  { id: "other", name: "Other labs & enterprise", compute: 64 },
];

export const HYP: Hyperscaler[] = [
  { id: "amzn", name: "Amazon / AWS", rating: "AA", capex: 200, prior: 125, aiRev: 38, totRev: 700, baseProfit: 82, mix: { anthropic: 0.55, openai: 0.03, other: 0.42 }, lev: 0.35, incM: 0.32, spread: 60, debtShare: 0.30, ten: [0.45, 0.25, 0.30] },
  { id: "goog", name: "Google / GCP", rating: "AA+", capex: 180, prior: 91, aiRev: 30, totRev: 420, baseProfit: 135, mix: { anthropic: 0.45, openai: 0.00, other: 0.55 }, lev: 0.25, incM: 0.35, spread: 50, debtShare: 0.18, ten: [0.45, 0.25, 0.30] },
  { id: "msft", name: "Microsoft / Azure", rating: "AAA", capex: 115, prior: 90, aiRev: 34, totRev: 310, baseProfit: 130, mix: { openai: 0.60, anthropic: 0.05, other: 0.35 }, lev: 0.30, incM: 0.38, spread: 45, debtShare: 0.15, ten: [0.40, 0.25, 0.35] },
  { id: "meta", name: "Meta", rating: "AA-", capex: 125, prior: 72, aiRev: 0, totRev: 210, baseProfit: 80, mix: {}, lev: 0.30, incM: 0.40, spread: 75, debtShare: 0.40, ten: [0.35, 0.25, 0.40] },
  { id: "orcl", name: "Oracle / OCI", rating: "BBB-", capex: 55.7, prior: 21.2, aiRev: 22, totRev: 65, baseProfit: 14, mix: { openai: 0.75, anthropic: 0.05, other: 0.20 }, lev: 0.90, incM: 0.28, spread: 180, debtShare: 0.85, ten: [0.50, 0.25, 0.25] },
  { id: "spcx", name: "SpaceX / xAI", rating: "B+", capex: 28.4, prior: 20.7, aiRev: 14, totRev: 32, baseProfit: 2, mix: { anthropic: 0.45, openai: 0.00, other: 0.55 }, lev: 0.55, incM: 0.25, spread: 450, debtShare: 0.70, ten: [0.70, 0.20, 0.10] },
];

export const SUP: Supplier[] = [
  { t: "chips", n: "NVIDIA", rt: "AA-", rev: 265, dc: 0.88, m: 0.60, lev: 0.9, cs: 0.05, cu: 0.15, ps: 0, spread: 60, cap: 0.02 },
  { t: "power", n: "GE Vernova", rt: "BBB+", rev: 46, dc: 0.25, m: 0.13, lev: 1.2, cs: 0.09, cu: 0.55, ps: 0, spread: 110, cap: 0.05 },
  { t: "power", n: "Vertiv", rt: "BB+", rev: 14, dc: 0.75, m: 0.19, lev: 1.4, cs: 0.10, cu: 0.35, ps: 0, spread: 210, cap: 0.04 },
  { t: "power", n: "Eaton", rt: "A-", rev: 30, dc: 0.22, m: 0.24, lev: 1.0, cs: 0.08, cu: 0.45, ps: 0, spread: 85, cap: 0.05 },
  { t: "power", n: "Schneider Electric", rt: "A-", rev: 44, dc: 0.20, m: 0.18, lev: 0.9, cs: 0.08, cu: 0.40, ps: 0, spread: 85, cap: 0.05 },
  { t: "power", n: "Siemens Energy", rt: "BBB", rev: 43, dc: 0.15, m: 0.09, lev: 1.3, cs: 0.10, cu: 0.60, ps: 0, spread: 120, cap: 0.05 },
  { t: "power", n: "Caterpillar", rt: "A", rev: 65, dc: 0.08, m: 0.19, lev: 1.1, cs: 0.09, cu: 0.30, ps: 0, spread: 75, cap: 0.04 },
  { t: "power", n: "Cummins", rt: "A+", rev: 34, dc: 0.07, m: 0.13, lev: 1.1, cs: 0.09, cu: 0.25, ps: 0, spread: 70, cap: 0.04 },
  { t: "util", n: "Constellation", rt: "BBB+", rev: 44, dc: 0.20, m: 0.15, lev: 0.7, cs: 0.05, cu: 0.70, ps: 0.55, spread: 115, cap: 0.14 },
  { t: "util", n: "Vistra", rt: "BB+", rev: 23, dc: 0.25, m: 0.24, lev: 0.8, cs: 0.05, cu: 0.60, ps: 0.70, spread: 200, cap: 0.12 },
  { t: "util", n: "Talen Energy", rt: "BB-", rev: 2.5, dc: 0.40, m: 0.20, lev: 1.0, cs: 0.05, cu: 0.65, ps: 0.75, spread: 290, cap: 0.18 },
  { t: "util", n: "NextEra", rt: "A-", rev: 27, dc: 0.12, m: 0.25, lev: 0.6, cs: 0.06, cu: 0.75, ps: 0.35, spread: 95, cap: 0.34 },
  { t: "util", n: "Dominion", rt: "BBB+", rev: 16, dc: 0.15, m: 0.22, lev: 0.5, cs: 0.05, cu: 0.80, ps: 0.25, spread: 115, cap: 0.32 },
  { t: "util", n: "AEP", rt: "BBB+", rev: 21, dc: 0.10, m: 0.20, lev: 0.5, cs: 0.05, cu: 0.80, ps: 0.25, spread: 115, cap: 0.30 },
  { t: "gas", n: "Williams", rt: "BBB", rev: 11, dc: 0.18, m: 0.34, lev: 1.1, cs: 0.06, cu: 0.55, ps: 0.30, spread: 120, cap: 0.30 },
  { t: "gas", n: "Energy Transfer", rt: "BBB-", rev: 83, dc: 0.03, m: 0.11, lev: 0.7, cs: 0.05, cu: 0.60, ps: 0.35, spread: 145, cap: 0.06 },
  { t: "gas", n: "Kinder Morgan", rt: "BBB", rev: 16, dc: 0.08, m: 0.26, lev: 0.8, cs: 0.05, cu: 0.65, ps: 0.30, spread: 125, cap: 0.16 },
  { t: "gas", n: "Cheniere", rt: "BBB", rev: 16, dc: 0.02, m: 0.30, lev: 0.6, cs: 0.05, cu: 0.80, ps: 0.45, spread: 135, cap: 0.22 },
  { t: "gas", n: "EQT", rt: "BBB", rev: 6, dc: 0.12, m: 0.28, lev: 1.3, cs: 0.06, cu: 0.35, ps: 0.75, spread: 140, cap: 0.38 },
  { t: "gas", n: "ExxonMobil", rt: "AA-", rev: 340, dc: 0.01, m: 0.12, lev: 0.6, cs: 0.05, cu: 0.70, ps: 0.25, spread: 55, cap: 0.07 },
  { t: "gas", n: "Chevron", rt: "AA-", rev: 195, dc: 0.015, m: 0.11, lev: 0.6, cs: 0.05, cu: 0.70, ps: 0.25, spread: 55, cap: 0.09 },
  { t: "tele", n: "AT&T", rt: "BBB", rev: 129, dc: 0.04, m: 0.18, lev: 0.5, cs: 0.06, cu: 0.50, ps: 0, spread: 130, cap: 0.18 },
  { t: "tele", n: "Verizon", rt: "BBB+", rev: 135, dc: 0.03, m: 0.22, lev: 0.5, cs: 0.06, cu: 0.50, ps: 0, spread: 120, cap: 0.12 },
  { t: "tele", n: "Lumen", rt: "B", rev: 12.2, dc: 0.30, m: 0.06, lev: 1.6, cs: 0.08, cu: 0.40, ps: 0, spread: 520, cap: 0.27 },
  { t: "tele", n: "Corning", rt: "BBB+", rev: 20, dc: 0.35, m: 0.18, lev: 1.2, cs: 0.08, cu: 0.35, ps: 0, spread: 115, cap: 0.06 },
  { t: "tele", n: "Crown Castle", rt: "BBB", rev: 6.5, dc: 0.08, m: 0.30, lev: 0.6, cs: 0.05, cu: 0.70, ps: 0, spread: 135, cap: 0.20 },
];

export const CAPEX_SPLIT = { chips: 0.52, power: 0.20, util: 0.08, gas: 0.03, tele: 0.05, other: 0.12 };

export const BASE_GAS = 0.25;

export const COEF: Coefficients = {
  sticky: { v: 0.35, lab: "Capex stickiness", hint: "Share of hyperscaler capex already committed (land, shells, signed turbine orders) and unresponsive to demand within one year." },
  finDur: { v: 6, lab: "Capex rate duration", hint: "Years of rate sensitivity applied to a levered builder. Higher = a rate move bites capex harder." },
  gdp: { v: 1.0, lab: "Non-AI demand index", hint: "Multiplier on the portion of supplier revenue unrelated to data centres." },
  depShare: { v: 0.85, lab: "Depreciable share", hint: "Fraction of capex that is equipment on a short depreciation clock rather than land and shells." },
  newInSvc: { v: 0.50, lab: "New capex in service", hint: "Share of this year's capex actually generating depreciation this year (half-year convention). The prior-year cohort is fully in service and cannot be switched off — that is the depreciation wall." },
  otherLab: { v: 64, lab: "Non-OpenAI/Anthropic demand", hint: "Compute demand in $B from everyone else. This is the floor under the system if both named labs fail." },
  supRate: { v: 2.5, lab: "Supplier rate drag", hint: "How much a supplier own-capex program contracts per 100bp of rate rise, scaled by its capex intensity." },
};

export const BASE_Y: BaseYields = { y10: 4.79, y20: 5.27, y30: 5.27 };

/** Per-tier tenor weights [10y, 20y, 30y] for supplier funding. */
export const TENOR: Record<Tier, [number, number, number]> = {
  chips: [0.70, 0.20, 0.10],
  power: [0.60, 0.25, 0.15],
  util: [0.20, 0.25, 0.55],
  gas: [0.30, 0.30, 0.40],
  tele: [0.40, 0.25, 0.35],
};

/** Deep-clone the seed into a fresh World. solve() never mutates it, but the
 *  UI's editable assumptions panel and live ingest both need their own copy. */
export function baseWorld(): World {
  return structuredClone({ LABS, HYP, SUP, COEF, CAPEX_SPLIT, BASE_GAS, BASE_Y, TENOR });
}

/** The base-case drivers. Matches SCEN.base in the prototype. */
export function baseDrivers(): Drivers {
  return {
    capex: 1.00, cost: 1.00, ai: 1.00, oa: 1.00, an: 1.00, pw: 1.00,
    life: 6, y10: 4.79, y20: 5.27, y30: 5.27, sm: 1.00, gas: 0.25,
  };
}
